<template>
  <el-drawer
    :visible.sync="drawerVisible"
    :size="drawerSize"
    :title="title"
    :append-to-body="true"
    custom-class="fuzzy-quote-preview-drawer"
  >
    <div class="fuzzy-quote-preview-document">
      <div class="container">

        <!-- 品牌头部 -->
        <div class="brand-header">
          <div class="brand-logo">
            <div class="logo-box">COSMO<br>Plat</div>
            <div class="brand-text">
              <div class="main">卡奥斯 COSMOPlat</div>
              <div class="sub">工业互联网平台 · 销供协同数字人</div>
            </div>
          </div>
          <div class="brand-doc-type">模糊询价框架报价预览</div>
        </div>

        <!-- 核心结论 -->
        <div class="hero-card">
          <div class="hero-title">模糊询价框架估算 · RFQ-EW-WM-AI15-PRELIMINARY</div>
          <div class="hero-main">
            易德龙科技 · AIoT 变频洗衣机智能控制器<br>
            客户仅一句话需求 · 无 BOM / 无 Gerber / 无工艺文件
          </div>
          <div class="hero-stats">
            <div class="hero-stat">
              <span class="val">3 / 10</span>
              <span class="lbl">信息完整度</span>
            </div>
            <div class="hero-stat">
              <span class="val">95%</span>
              <span class="lbl">最高案例相似度</span>
            </div>
            <div class="hero-stat">
              <span class="val">340 - 420</span>
              <span class="lbl">标准型框架区间（元/台）</span>
            </div>
            <div class="hero-stat">
              <span class="val">中高</span>
              <span class="lbl">框架估算置信度</span>
            </div>
          </div>
          <div class="hero-badge">
            <span>PRELIMINARY</span> · <span>框架估算，非正式精确报价</span> · <span>补齐资料后可收窄至 ±5%</span>
          </div>
        </div>

        <!-- 使用边界 -->
        <div class="risk-bar">
          <div class="icon">⚠️</div>
          <div class="content">
            <div class="title">框架报价使用边界</div>
            <div class="desc">
              ① 当前没有 BOM、Gerber 和工艺文件，无法承诺精确到分项成本的正式报价<br>
              ② 本区间用于客户早期预算沟通和内部立项评估，销售不可直接转成正式报价单<br>
              ③ 收到 BOM、Gerber 或关键功能参数后，可自动切换正式报价流程，预计 2-4 小时形成精确方案
            </div>
          </div>
        </div>

        <!-- 产物 1: 需求识别 -->
        <details class="deliverable-card" open>
          <summary>
            <div class="card-num">1</div>
            <div class="card-info">
              <div class="card-title">需求识别报告</div>
              <div class="card-meta">7 维度语义解析 · 信息完整度评分 · 模式判断结论</div>
            </div>
            <div class="card-status status-warn">模糊询价</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>解析维度</th><th>识别结果</th><th>来源</th><th>置信度</th></tr></thead>
              <tbody>
                <tr><td>产品名称</td><td>AIoT 变频洗衣机智能控制器</td><td>客户原文</td><td class="td-success">98%</td></tr>
                <tr><td>产品类别</td><td>家电控制器 &gt; 洗衣机主控</td><td>行业知识库</td><td class="td-success">95%</td></tr>
                <tr><td>电机驱动</td><td>变频（BLDC / PMSM）</td><td>“变频”关键词</td><td>90%</td></tr>
                <tr><td>通信方式</td><td>待确认，AIoT 可能包含 WiFi</td><td>行业推断</td><td><span class="tag tag-orange">60%</span></td></tr>
                <tr><td>传感功能</td><td>待确认，洗衣机常见水温、水位、称重</td><td>行业默认</td><td><span class="tag tag-orange">70%</span></td></tr>
                <tr><td>显示方式</td><td>待确认</td><td>未知</td><td><span class="tag tag-red">40%</span></td></tr>
                <tr><td>芯片平台</td><td>未提及，暂按 STM32 系列推断</td><td>行业基准</td><td><span class="tag tag-red">30%</span></td></tr>
                <tr><td>首批数量</td><td>500 台</td><td>客户原文</td><td class="td-success">98%</td></tr>
                <tr><td>年预估量</td><td>未提及</td><td>—</td><td>—</td></tr>
                <tr><td>目标价 / 预算</td><td>未提及</td><td>—</td><td>—</td></tr>
                <tr><td>认证与交付</td><td>未提及，暂按 CCC 与常用交付条款预留</td><td>行业默认</td><td><span class="tag tag-orange">60%</span></td></tr>
              </tbody>
            </table>
            <div class="calc-card calc-card-warning">
              <div class="calc-title calc-title-warning">模式判断结论</div>
              <p>有效信息维度不足 3 项，BOM 完整度为 0，不进入正式精确报价流程，启动“模糊询价快速估算模式”。</p>
            </div>
          </div>
        </details>

        <!-- 产物 2: 历史案例匹配 -->
        <details class="deliverable-card" open>
          <summary>
            <div class="card-num">2</div>
            <div class="card-info">
              <div class="card-title">历史案例匹配表</div>
              <div class="card-meta">Top 3 相似案例 · 产品 / 功能域 / 历史报价 / 参考价值</div>
            </div>
            <div class="card-status status-done">已匹配</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>排序</th><th>历史案例</th><th>产品</th><th>核心匹配点</th><th>相似度</th><th>历史报价</th><th>参考价值</th></tr></thead>
              <tbody>
                <tr>
                  <td class="td-highlight">1</td>
                  <td><strong>RFQ-EW-WM-AI15</strong></td>
                  <td>变频洗衣机控制器</td>
                  <td>变频 + AIoT + WiFi + 全传感</td>
                  <td class="td-success">95%</td>
                  <td>318.89 元/台</td>
                  <td><span class="tag tag-green">★★★★★</span></td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>RFQ-EW-WM-AI12</td>
                  <td>波轮洗衣机控制器</td>
                  <td>同为洗衣机、基础变频、成本结构接近</td>
                  <td>82%</td>
                  <td>285.00 元/台</td>
                  <td><span class="tag tag-blue">★★★★☆</span></td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>RFQ-EW-WM-AI08</td>
                  <td>滚筒洗衣机控制器</td>
                  <td>变频 + WiFi，产品结构差异较大</td>
                  <td>65%</td>
                  <td>310.00 元/台</td>
                  <td><span class="tag tag-orange">★★★☆☆</span></td>
                </tr>
              </tbody>
            </table>
            <div class="calc-card calc-card-primary">
              <div class="calc-title">核心发现</div>
              <p>发现内部已有完整量产方案 RFQ-EW-WM-AI15，包括 87 项 BOM、成本数据、协议价和量产验证，可作为本次框架报价的核心基准。</p>
            </div>
          </div>
        </details>

        <!-- 产物 3: 框架报价区间 -->
        <details class="deliverable-card" open>
          <summary>
            <div class="card-num">3</div>
            <div class="card-info">
              <div class="card-title">框架报价区间</div>
              <div class="card-meta">经济型 / 标准型 / 高端型三档 · 含关键假设和不确定性缓冲</div>
            </div>
            <div class="card-status status-warn">框架估算</div>
          </summary>
          <div class="card-body">
            <div class="path-card">
              <div class="path-title">经济型方案</div>
              <div class="path-price">280 - 340 元/台</div>
              <div class="path-desc">F103 主控 · 基础变频 · 基础传感 · 无通信 · 无显示<br>适合客户仅需要基础控制功能、对智能化要求较低的场景。</div>
            </div>
            <div class="path-card recommended">
              <div class="path-tag">推荐基准</div>
              <div class="path-title">标准型方案</div>
              <div class="path-price">340 - 420 元/台</div>
              <div class="path-desc">F407 主控 · 完整变频驱动 · WiFi · LCD · 全传感<br>与内部历史方案 AI15 功能域最接近，适合作为首轮客户预算沟通区间。</div>
            </div>
            <div class="path-card">
              <div class="path-title">高端型方案</div>
              <div class="path-price">420 - 500 元/台</div>
              <div class="path-desc">全功能配置 · 认证包 · 冗余设计 · 更高功率器件<br>适合有出口认证、可靠性冗余或更大功率段要求的项目。</div>
            </div>
            <div class="calc-card">
              <div class="calc-title">框架估算基准</div>
              <p>以 RFQ-EW-WM-AI15 为内部基准，结合功能域差异和缺失信息建立缓冲。当前结果用于预算沟通，不替代正式精确报价。</p>
            </div>
          </div>
        </details>

        <!-- 产物 4: 区间收窄条件 -->
        <details class="deliverable-card">
          <summary>
            <div class="card-num">4</div>
            <div class="card-info">
              <div class="card-title">区间收窄条件与敏感性</div>
              <div class="card-meta">关键变量对价格区间的影响 · 资料补齐后的收窄路径</div>
            </div>
            <div class="card-status status-info">待补齐</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>变量</th><th>未确认状态</th><th>价格影响</th><th>资料补齐后动作</th></tr></thead>
              <tbody>
                <tr><td>芯片平台</td><td>F103 / F407 未确认</td><td>±15 - 20 元/台</td><td>锁定主控型号，匹配协议价与替代料</td></tr>
                <tr><td>电机功率</td><td>500W / 1000W / 1500W</td><td>±20 - 35 元/台</td><td>确认功率段，重算 MOSFET、散热和变压器</td></tr>
                <tr><td>通信方式</td><td>WiFi / 蓝牙 / 两者都要</td><td>±12 - 25 元/台</td><td>确认通信模组与认证要求</td></tr>
                <tr><td>测试标准</td><td>基础测试 / 48h 老化 / 三防</td><td>±8 - 15 元/台</td><td>确认测试等级并更新制造费用</td></tr>
                <tr><td>显示方式</td><td>LCD / LED / 无显示</td><td>±10 - 20 元/台</td><td>确认显示模组和结构件</td></tr>
              </tbody>
            </table>
            <div class="calc-card calc-card-success">
              <div class="calc-title calc-title-success">区间收窄条件</div>
              <p>收到 BOM 清单后，框架区间可收窄至正式估算区间；完成 BOM、Gerber、功率和通信确认后，可进入正式报价流程，预计 2-4 小时输出完整精确方案。</p>
            </div>
          </div>
        </details>

        <!-- 产物 5: 资料补齐向导 -->
        <details class="deliverable-card">
          <summary>
            <div class="card-num">5</div>
            <div class="card-info">
              <div class="card-title">资料补齐向导</div>
              <div class="card-meta">高 / 中 / 低优先级 · 默认值、替代方案和沟通话术</div>
            </div>
            <div class="card-status status-warn">需销售跟进</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>优先级</th><th>需要确认</th><th>为什么关键</th><th>默认方案 / 替代路径</th><th>建议话术</th></tr></thead>
              <tbody>
                <tr>
                  <td><span class="tag tag-red">高</span></td>
                  <td>BOM 清单初稿</td>
                  <td>影响约 60% 成本精度</td>
                  <td>无 BOM 可评估竞品样品逆向拆解</td>
                  <td>“哪怕只有主控型号也可以，我们先自动补全。”</td>
                </tr>
                <tr>
                  <td><span class="tag tag-red">高</span></td>
                  <td>电机功率范围</td>
                  <td>影响功率器件、散热与变压器选型</td>
                  <td>按 8kg 洗衣机推断约 800W</td>
                  <td>“500W / 1000W / 1500W，或有目标机型也可以。”</td>
                </tr>
                <tr>
                  <td><span class="tag tag-red">高</span></td>
                  <td>通信方式</td>
                  <td>影响通信模组与认证成本</td>
                  <td>默认不含通信，按经济型下限估算</td>
                  <td>“WiFi / 蓝牙 / 两者都要 / 暂不需要？”</td>
                </tr>
                <tr>
                  <td><span class="tag tag-orange">中</span></td>
                  <td>Gerber / PCB 尺寸</td>
                  <td>影响板厂、SMT 和可靠性成本</td>
                  <td>按 4 层 FR-4 沉金 1.6mm 估算</td>
                  <td>“有 Gerber 最好，没有也可以先按常用规格估算。”</td>
                </tr>
                <tr>
                  <td><span class="tag tag-orange">中</span></td>
                  <td>目标价 / 预算区间</td>
                  <td>帮助校准方案档次</td>
                  <td>提供经济 / 标准 / 高端三档供选择</td>
                  <td>“您更关注成本还是功能完整度？”</td>
                </tr>
                <tr>
                  <td><span class="tag tag-green">低</span></td>
                  <td>认证、年量与账期</td>
                  <td>影响认证费用、年框折扣和资金占用</td>
                  <td>默认 CCC，年量按首批 10 倍预估</td>
                  <td>“出口认证和年度预测量方便一起确认吗？”</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>

        <!-- 产物 6: 可发送客户的框架说明 -->
        <details class="deliverable-card">
          <summary>
            <div class="card-num">6</div>
            <div class="card-info">
              <div class="card-title">可发送客户的框架说明</div>
              <div class="card-meta">可对外发送 · 必须保留“框架估算”声明 · 不展示内部成本</div>
            </div>
            <div class="card-status status-info">可用于早期沟通</div>
          </summary>
          <div class="card-body">
            <div class="quote-preview">
易德龙科技家电有限公司：

您好。根据贵司目前提供的“AIoT 变频洗衣机智能控制器、首批 500 台”需求，
我司已完成一轮历史方案匹配和功能域估算，供贵司内部立项和预算沟通参考：

经济型方案：280 - 340 元/台
适用于基础变频、基础传感、无通信和无显示配置。

标准型方案：340 - 420 元/台
适用于 F407 主控、完整变频驱动、WiFi、LCD 和全传感配置。

高端型方案：420 - 500 元/台
适用于全功能、认证包、冗余设计及更高功率器件配置。

说明：
1. 以上为框架估算区间，不是正式精确报价；
2. 具体价格受 BOM、PCB 工艺、电机功率、通信方式、测试标准和交付条款影响；
3. 补齐 BOM 或 Gerber 后，我司可在 2-4 小时内重新核算并输出正式报价方案。

期待进一步确认技术资料与项目需求。

卡奥斯 COSMOPlat · 商机报价
            </div>
          </div>
        </details>

        <!-- 产物 7: 临时归档与模式切换 -->
        <details class="deliverable-card">
          <summary>
            <div class="card-num">7</div>
            <div class="card-info">
              <div class="card-title">临时归档与模式切换</div>
              <div class="card-meta">PRELIMINARY 状态 · 提醒计划 · 正式报价衔接条件</div>
            </div>
            <div class="card-status status-done">已归档</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>项目</th><th>当前状态 / 规则</th><th>后续动作</th></tr></thead>
              <tbody>
                <tr><td>询价编号</td><td>RFQ-EW-WM-AI15-PRELIMINARY</td><td>正式报价后生成正式询价 / 报价单号</td></tr>
                <tr><td>当前状态</td><td><span class="tag tag-orange">资料待补齐</span></td><td>保留为潜在商机，不误发正式报价</td></tr>
                <tr><td>T+3 天</td><td>客户资料未补</td><td>向销售推送一次跟进提醒</td></tr>
                <tr><td>T+7 天</td><td>客户仍未补资料</td><td>升级提醒销售总监，标记潜在流失风险</td></tr>
                <tr><td>BOM 完整度 ≥ 50%</td><td>达到模式切换条件</td><td>继承需求与历史匹配结果，进入正式报价解析</td></tr>
                <tr><td>收到 Gerber + 数量</td><td>满足 PCB 与数量基础</td><td>自动提取 PCB 参数并启动成本核算</td></tr>
                <tr><td>收到竞品样品</td><td>可启动逆向拆解</td><td>生成拆解工单，3-5 天输出 BOM 与成本估算</td></tr>
              </tbody>
            </table>
          </div>
        </details>

        <!-- 产物 8: 数据来源与合规边界 -->
        <details class="deliverable-card">
          <summary>
            <div class="card-num">8</div>
            <div class="card-info">
              <div class="card-title">数据来源与合规边界</div>
              <div class="card-meta">内部资料、客户可发送资料与状态标记分离</div>
            </div>
            <div class="card-status status-info">规则说明</div>
          </summary>
          <div class="card-body">
            <table>
              <thead><tr><th>产物 / 信息</th><th>接收人</th><th>是否可对外</th><th>边界说明</th></tr></thead>
              <tbody>
                <tr><td>需求识别报告</td><td>销售 / 内部</td><td>否</td><td>包含信息完整度、推断项和置信度</td></tr>
                <tr><td>历史案例匹配表</td><td>销售 / 内部</td><td>否</td><td>包含内部历史报价与项目信息</td></tr>
                <tr><td>框架报价区间</td><td>销售 / 客户</td><td>可发送</td><td>必须标注“框架估算，非正式精确报价”</td></tr>
                <tr><td>资料补齐向导</td><td>销售 / 客户</td><td>可转化发送</td><td>销售确认措辞后再发送</td></tr>
                <tr><td>临时归档状态</td><td>销售 / 系统</td><td>否</td><td>用于提醒、跟踪和潜在流失管理</td></tr>
              </tbody>
            </table>
            <div class="calc-card calc-card-warning">
              <div class="calc-title calc-title-warning">重要声明</div>
              <p>模糊询价模式下不输出正式精确报价，不承诺分项成本，不允许将框架区间直接包装成客户正式报价单。</p>
            </div>
          </div>
        </details>

        <!-- 底部信息 -->
        <div class="doc-footer">
          <p>卡奥斯 COSMOPlat · 销供协同数字人 · 商机报价</p>
          <p>询价单号：RFQ-EW-WM-AI15-PRELIMINARY ｜ 状态：框架估算 / 资料待补齐</p>
          <p>版本 V1.0</p>
        </div>

      </div>
    </div>
  </el-drawer>
</template>

<script>
export default {
  name: 'FuzzyQuotePreviewDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '模糊询价框架报价预览 · RFQ-EW-WM-AI15-PRELIMINARY'
    }
  },
  data() {
    return {
      drawerSize: 'min(1180px, 96vw)'
    }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
        if (!val) {
          this.$emit('close')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fuzzy-quote-preview-document {
  --ink: #172033;
  --muted: #64748b;
  --sub: #8593a3;
  --line: #dfe6f1;
  --bg: #f3f6fb;
  --surface: #fff;
  --surface-soft: #f1f5f9;
  --primary: #d97706;
  --primary-deep: #9a4f08;
  --primary-soft: #fff3e3;
  --accent: #f59e0b;
  --accent-soft: #fff7e8;
  --success: #16a34a;
  --success-soft: #e6f4ea;
  --warning: #d97706;
  --warning-soft: #fff3e3;
  --danger: #dc2626;
  --danger-soft: #fdeaea;
  --cosmos-blue: #0052d9;
  --cosmos-dark: #0a1a3a;

  min-height: 100%;
  background: var(--bg);
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 14px;
  line-height: 1.7;

  * { box-sizing: border-box; }

  .container { max-width: 1100px; margin: 0 auto; padding: 32px 32px 60px; }

  /* 品牌头部 */
  .brand-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 28px;
    background: linear-gradient(135deg, #3d2608 0%, #b35b08 55%, #e59a1f 100%);
    border-radius: 10px 10px 0 0;
    margin: 0 -32px 0 -32px; width: calc(100% + 64px);
  }
  .brand-logo { display: flex; align-items: center; gap: 14px; }
  .brand-logo .logo-box {
    width: 44px; height: 44px; background: #fff; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800; color: #b35b08; letter-spacing: -0.5px;
  }
  .brand-logo .brand-text { color: #fff; }
  .brand-logo .brand-text .main { font-size: 18px; font-weight: 800; letter-spacing: 1px; }
  .brand-logo .brand-text .sub { font-size: 11px; opacity: 0.75; margin-top: 1px; }
  .brand-doc-type { color: #ffd89a; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 2px; }

  /* 核心结论卡 */
  .hero-card {
    background: linear-gradient(135deg, #352107 0%, #8f4a09 58%, #d98916 100%);
    color: #fff; padding: 32px 36px; border-radius: 14px;
    margin: 28px 0 24px; position: relative; overflow: hidden;
  }
  .hero-card::before {
    content: ''; position: absolute; top: -50%; right: -10%; width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  }
  .hero-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: #ffd89a; margin-bottom: 12px; }
  .hero-main { font-size: 24px; font-weight: 800; line-height: 1.4; margin-bottom: 16px; }
  .hero-stats { display: flex; gap: 32px; flex-wrap: wrap; }
  .hero-stat { display: flex; flex-direction: column; }
  .hero-stat .val { font-size: 22px; font-weight: 800; }
  .hero-stat .lbl { font-size: 12px; opacity: 0.7; margin-top: 2px; }
  .hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.15); padding: 6px 14px; border-radius: 20px;
    font-size: 12px; font-weight: 600; margin-top: 16px;
  }

  /* 风险条 */
  .risk-bar {
    background: var(--danger-soft); border-left: 4px solid var(--danger);
    padding: 14px 18px; border-radius: 8px; margin: 16px 0;
    display: flex; align-items: flex-start; gap: 10px;
  }
  .risk-bar .icon { color: var(--danger); font-size: 18px; flex-shrink: 0; }
  .risk-bar .content { flex: 1; }
  .risk-bar .title { font-weight: 700; color: var(--danger); font-size: 14px; }
  .risk-bar .desc { color: var(--ink); font-size: 13px; margin-top: 4px; }

  /* 折叠卡片 */
  .deliverable-card {
    background: var(--surface); border: 1px solid var(--line);
    border-radius: 12px; margin-bottom: 14px; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  }
  .deliverable-card summary {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 20px; cursor: pointer; list-style: none;
    background: var(--surface-soft);
    border-bottom: 1px solid var(--line);
    transition: background 0.2s;
  }
  .deliverable-card summary:hover { background: #e8eef5; }
  .deliverable-card summary::-webkit-details-marker { display: none; }
  .card-num {
    width: 28px; height: 28px; border-radius: 50%;
    background: var(--primary); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0;
  }
  .card-info { flex: 1; }
  .card-title { display: flex; justify-content: flex-start; font-size: 15px; font-weight: 700; color: var(--ink); }
  .card-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }
  .card-status {
    padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;
    flex-shrink: 0;
  }
  .status-done { background: var(--success-soft); color: var(--success); }
  .status-warn { background: var(--warning-soft); color: var(--warning); }
  .status-info { background: var(--primary-soft); color: var(--primary); }
  .card-body { padding: 20px; }

  /* 表格 */
  /* 表格 */
  table {
    width: 100%; border-collapse: separate; border-spacing: 0;
    font-size: 12px; margin: 14px 0 18px; background: #fff;
    border: 1px solid var(--line); border-radius: 8px;
    overflow: hidden;
  }
  th {
    background: var(--surface-soft); font-weight: 600;
    padding: 14px 14px; text-align: left; border-bottom: 2px solid var(--line);
    color: var(--ink); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
  }
  td {
    padding: 14px 14px; border-bottom: 1px solid #f0f0f0;
    vertical-align: top;
  }
  tr:hover td { background: #fafbfc; }
  .td-num { font-variant-numeric: tabular-nums; text-align: right; }
  .td-center { text-align: center; }
  .td-highlight { font-weight: 700; color: var(--primary); }
  .td-danger { color: var(--danger); font-weight: 600; }
  .td-success { color: var(--success); font-weight: 600; }

  /* 计算卡片 */
  .calc-card {
    background: var(--surface-soft); border-radius: 8px;
    padding: 14px 16px; margin: 10px 0;
  }
  .calc-card-primary { border: 2px solid var(--primary); }
  .calc-card-warning { background: var(--warning-soft); }
  .calc-card-success { background: var(--success-soft); }
  .calc-title { font-weight: 700; font-size: 13px; margin-bottom: 8px; color: var(--primary-deep); }
  .calc-title-warning { color: var(--warning); }
  .calc-title-success { color: var(--success); }
  .calc-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .calc-row .lbl { color: var(--muted); }
  .calc-row .val { font-variant-numeric: tabular-nums; font-weight: 600; }
  .calc-divider { border-top: 1px dashed var(--line); margin: 6px 0; }
  .calc-total { font-weight: 800; color: var(--primary); font-size: 14px; }

  /* 报价单样式 */
  .quote-preview {
    background: #fff; border: 2px solid var(--line); border-radius: 8px;
    padding: 24px; font-family: "Courier New", monospace; font-size: 12px; line-height: 1.8;
    white-space: pre-wrap;
  }

  /* 路径卡片 */
  .path-card {
    border: 2px solid var(--line); border-radius: 10px;
    padding: 14px 16px; margin: 10px 0;
    position: relative;
  }
  .path-card.recommended { border-color: var(--success); background: var(--success-soft); }
  .path-tag {
    position: absolute; top: -10px; right: 12px;
    background: var(--success); color: #fff;
    padding: 2px 10px; border-radius: 10px;
    font-size: 11px; font-weight: 700;
  }
  .path-title { font-weight: 700; font-size: 14px; margin-bottom: 6px; }
  .path-desc { font-size: 12px; color: var(--muted); }
  .path-price { font-size: 18px; font-weight: 800; color: var(--primary); margin: 8px 0; }

  /* 小标签 */
  .tag {
    display: inline-flex; align-items: center;
    padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;
    margin-right: 4px;
  }
  .tag-green { background: var(--success-soft); color: var(--success); }
  .tag-red { background: var(--danger-soft); color: var(--danger); }
  .tag-orange { background: var(--warning-soft); color: var(--warning); }
  .tag-blue { background: var(--primary-soft); color: var(--primary); }

  h3 { font-size: 17px; font-weight: 700; color: var(--primary-deep); margin: 24px 0 12px; }
  h4 { font-size: 14px; font-weight: 700; color: var(--ink); margin: 16px 0 8px; }
  p { margin: 8px 0; font-size: 13px; color: var(--ink); }

  .doc-footer {
    text-align: center; margin-top: 40px; padding-top: 20px;
    border-top: 1px solid var(--line); color: var(--muted); font-size: 12px;
  }
}
</style>
